import { Controller } from "react-hook-form";
import {
  Addon,
  Item,
  MenuOptionItem,
  type MultiSelection,
  MultiSelectMenu,
  Select,
  Switch,
  TextField,
  VStack,
} from "widgets";
import { fileNameFieldValidation } from "widgets/utils";

import { useExportForm } from "./hooks.ts";

export const ExportForm = () => {
  const { ui, control, suffixLabel, setValue } = useExportForm();

  return (
    <form data-testid="export-form">
      <VStack spacing={15}>
        {/* Content select */}
        <Controller
          control={control}
          name="contentSelect"
          render={({ field }) => (
            <Select
              label="Content"
              items={ui.contentOptions}
              selectedKey={field.value}
              onSelectionChange={(id) => {
                field.onChange(id);
                const meta = ui.contentOptions.find((o) => o.id === id);
                // Keep both in sync
                setValue("contentOptionMeta", meta, { shouldValidate: true, shouldDirty: false });
              }}
              isRequired
              width={180}
              data-testid="content-select"
            >
              {(item) => (
                <Item key={item.id} textValue={item.label}>
                  <MenuOptionItem title={item.label} />
                </Item>
              )}
            </Select>
          )}
        />

        {/* Format select*/}
        {ui.showFormatSelect && (
          <Controller
            control={control}
            name="formatSelect"
            render={({ field }) => (
              <Select
                label="Format"
                items={ui.formatOptions}
                selectedKey={field.value}
                onSelectionChange={field.onChange}
                isRequired
                width={180}
                data-testid="format-select"
              >
                {(item) => (
                  <Item key={item.value} textValue={item.label}>
                    <MenuOptionItem title={item.label} />
                  </Item>
                )}
              </Select>
            )}
          />
        )}

        {/* Fasta header select */}
        {ui.showFastaHeaderSelect && (
          <Controller
            control={control}
            name="fastaHeaderSelect"
            render={({ field }) => (
              <Select
                label="FASTA header template"
                items={ui.fastaHeaderOptions}
                selectedKey={field.value}
                onSelectionChange={field.onChange}
                isRequired
                width={180}
                data-testid="fasta-header-select"
              >
                {(item) => (
                  <Item key={item.value} textValue={item.label}>
                    <MenuOptionItem title={item.label} />
                  </Item>
                )}
              </Select>
            )}
          />
        )}

        {/* Compressed checkbox */}
        {ui.showCompressedCheckbox && (
          <Controller
            control={control}
            name="compressed"
            render={({ field }) => (
              <Switch
                isSelected={!!field.value}
                onChange={field.onChange}
                label="Compressed"
                longDescription={"Compressed format to reduce download time."}
                data-testid="compressed-switch"
              />
            )}
          />
        )}

        {/* Reverse complement checkbox */}
        {ui.showReverseCheckbox && (
          <Controller
            control={control}
            name="reverse"
            render={({ field }) => (
              <Switch
                isSelected={!!field.value}
                onChange={field.onChange}
                label="Reverse complement"
                longDescription={"Reverse complement of the sequence."}
                data-testid="reverse-complement-switch"
              />
            )}
          />
        )}

        {/* Feature types multiselect */}
        {ui.showFeatureTypeSelect && (
          <Controller
            control={control}
            name="featureTypes"
            rules={{
              required: "Feature types are required",
              validate: {
                notEmpty: (v) => (v?.size ?? 0) > 0 || "Selection cannot be empty",
              },
            }}
            render={({ field, fieldState }) => (
              <MultiSelectMenu
                label={"Feature types"}
                isRequired={true}
                errorMessage={fieldState.error?.message}
                isInvalid={fieldState.invalid}
                items={ui.featureTypesOptions}
                rendered={(keys: MultiSelection) => {
                  return [...keys].map((key) => ui.featureTypesOptions.get(String(key))?.name).join(", ");
                }}
                onSelectionChange={field.onChange}
                selectedKeys={new Set(field.value?.keys())}
                width={180}
                data-testid="feature-types-select"
              >
                {([key, item]) => {
                  return (
                    <Item key={key} textValue={item.name}>
                      <MenuOptionItem title={item.name} />
                    </Item>
                  );
                }}
              </MultiSelectMenu>
            )}
          />
        )}

        {/* Field name */}
        <Controller
          name="fileName"
          control={control}
          rules={{
            required: "File name is required",
            validate: {
              notEmpty: (v) => (v?.trim()?.length ?? 0) > 0 || "File name cannot be empty",
              allowedChars: fileNameFieldValidation,
            },
          }}
          render={({ field, fieldState }) => (
            <TextField
              label="File Name"
              placeholder={"Please provide a name for the file to be downloaded"}
              value={field.value ?? ""}
              onChange={field.onChange}
              action={<Addon label={suffixLabel} placement="suffix" />}
              errorMessage={fieldState.error?.message}
              isInvalid={fieldState.invalid}
              isRequired
              data-testid="file-name"
            />
          )}
        />
      </VStack>
    </form>
  );
};
